import { openModal } from 'actions/modal';
import { getKitParents, getTemplateParents } from 'actions/requests';
import Button from 'components/atoms/Button';
import { DiscoverItem, DiscoverItemDescriptor, DiscoverItemType } from 'utils/gloo-types';
import { useMemo } from 'react';
import { useThunkDispatch } from 'utils/reduxHook';

interface DiscoverItemArchiveButtonProps {
  descriptor: DiscoverItemDescriptor;
  onArchivedAtChange: (archivedAt?: Date) => void;
}

const DiscoverItemArchiveButton = ({ descriptor, onArchivedAtChange }: DiscoverItemArchiveButtonProps) => {
  const dispatch = useThunkDispatch();

  const onClick = async () => {
    let parents: DiscoverItem[];
    switch (descriptor.type) {
      // Hubs cannot have parents
      case DiscoverItemType.HUB:
        parents = [];
        break;
      case DiscoverItemType.KIT:
        parents = await dispatch(getKitParents(descriptor.id));
        break;
      case DiscoverItemType.TEMPLATE:
        parents = await dispatch(getTemplateParents(descriptor.id));
        break;
    }

    dispatch(openModal('ArchiveDiscoverItemModal', { descriptor, onArchivedAtChange, parents }));
  };

  return (
    <Button type="secondary" dataDurableId="discoverItemArchiveToggle" onClick={onClick}>
      Archive
    </Button>
  );
};

interface DiscoverItemUnarchiveButtonProps {
  descriptor: DiscoverItemDescriptor;
  onArchivedAtChange: (archivedAt?: Date) => void;
}

const DiscoverItemUnarchiveButton = ({ descriptor, onArchivedAtChange }: DiscoverItemUnarchiveButtonProps) => {
  const dispatch = useThunkDispatch();

  const onClick = () => {
    dispatch(openModal('UnarchiveDiscoverItemModal', { descriptor, onArchivedAtChange }));
  };

  return (
    <Button type="secondary" dataDurableId="discoverItemArchiveToggle" onClick={onClick}>
      Unarchive
    </Button>
  );
};

interface DiscoverItemArchiveToggleButtonProps {
  descriptor: DiscoverItemDescriptor;
  archivedAt: Date | null;
  onArchivedAtChange: (archivedAt?: Date) => void;
}

const DiscoverItemArchiveToggleButton = ({
  descriptor,
  archivedAt,
  onArchivedAtChange,
}: DiscoverItemArchiveToggleButtonProps) =>
  useMemo(
    () =>
      archivedAt ? (
        <DiscoverItemUnarchiveButton descriptor={descriptor} onArchivedAtChange={onArchivedAtChange} />
      ) : (
        <DiscoverItemArchiveButton descriptor={descriptor} onArchivedAtChange={onArchivedAtChange} />
      ),
    [descriptor.id, descriptor.type, archivedAt],
  );

export default DiscoverItemArchiveToggleButton;
